import { ChangeEvent, useState } from "react";
import { useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  TextField,
  MenuItem,
  Select,
  Button,
  FormControlLabel,
  Box,
  Tab,
  Tabs,
  Checkbox,
  CardMedia,
  IconButton,
  Input,
  Radio,
  RadioGroup,
  Autocomplete,
  createFilterOptions,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Avatar,
} from "@mui/material";
import { Autocomplete as GoogleAutocomplete } from "@react-google-maps/api";
import EditIcon from "@mui/icons-material/Edit";
import CloseIcon from "@mui/icons-material/Close";
import EditText from "../../components/editText";
import {
  useUpdateProductMutation,
  useLazyGetProductByIdQuery,
} from "../../service/product";
import { useFormik } from "formik";
import * as Yup from "yup";
import { UploadMedia } from "../../utils/uploadMedia";
import { showError, showToast } from "../../constants/toasts";
import {
  useGetCatDropDownListQuery,
  useGetCategoryQuery,
} from "../../service/category";
import { COLORS } from "../../constants/enums";
import { useGetBoostPlanDropDownListQuery } from "../../service/boostPlans";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function CustomTabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <>{children}</>}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
}

type DayOfWeek =
  | "Monday"
  | "Tuesday"
  | "Wednesday"
  | "Thursday"
  | "Friday"
  | "Saturday"
  | "Sunday";

interface Availability {
  enabled: boolean;
  start: string;
  end: string;
}

function prune<T>(obj: T): T | undefined {
  if (obj == null) return obj;
  if (Array.isArray(obj)) {
    const arr = obj
      .map((v) => prune(v))
      .filter(
        (v) =>
          v === 0 ||
          v === false ||
          Boolean(v && (typeof v !== "object" || Object.keys(v).length))
      );
    return (arr.length ? arr : undefined) as T | undefined;
  }
  if (typeof obj === "object") {
    const out: any = {};
    Object.entries(obj as Record<string, any>).forEach(([k, v]) => {
      const pruned = prune(v);
      const isEmptyString = pruned === "";
      if (pruned === undefined || pruned === null || isEmptyString) return;
      out[k] = pruned;
    });
    return (Object.keys(out).length ? out : undefined) as T | undefined;
  }
  return obj;
}

const AddProduct = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);
  const [status, setStatus] = useState("default");
  const [region, setRegion] = useState("default");
  const [valueTabs, setValueTabs] = useState(0);
  const handleChangeTabs = (event: React.SyntheticEvent, newValue: number) => {
    setValueTabs(newValue);
  };

  const [alertMessage, setAlertMessage] = useState("");
  const [showAlert, setShowAlert] = useState(false);
  const [alertType, setAlertType] = useState(0);
  const [image, setImage] = useState<string>("");
  const [description, setDescription] = useState("");
  const [fileName, setFileName] = useState<string>("");
  const [condition, setCondition] = useState<"New" | "Used">("New");
  const [color, setColor] = useState("default");
  const [bulkPrice, setBulkPrice] = useState<string>("");
  const [features, setFeatures] = useState([
    {
      logo: "/static/images/tick_icon.svg",
      title: "Verified",
      description: "Yes",
    },
  ]);
  const [featureOptions, setFeatureOptions] = useState<string[]>(["Verified"]);
  const filter = createFilterOptions<string>();
  const [editIdx, setEditIdx] = useState<number | null>(null);
  const [editDraftLogo, setEditDraftLogo] = useState<string>("");
  const [editDraftDescription, setEditDraftDescription] = useState<string>("");
  const [googleAutocomplete, setGoogleAutocomplete] = useState<any>();
  const onGoogleLoad = (autocompleteObj: any) => {
    setGoogleAutocomplete(autocompleteObj);
  };
  const onGooglePlaceChanged = () => {
    if (!googleAutocomplete) return;
    const place = googleAutocomplete.getPlace();
    if (!place) return;
    const formatted = place.formatted_address || place.name || "";
    formik.setFieldValue("address", formatted);
    setAddressObj((prev: any) => ({
      ...(prev || {}),
      addressLine1: formatted,
    }));
  };

  const [addressObj, setAddressObj] = useState<any>(null);

  const [availability, setAvailability] = useState<
    Record<DayOfWeek, Availability>
  >({
    Monday: { enabled: true, start: "09:00", end: "18:00" },
    Tuesday: { enabled: true, start: "09:00", end: "18:00" },
    Wednesday: { enabled: true, start: "09:00", end: "18:00" },
    Thursday: { enabled: true, start: "09:00", end: "18:00" },
    Friday: { enabled: true, start: "09:00", end: "18:00" },
    Saturday: { enabled: false, start: "09:00", end: "18:00" },
    Sunday: { enabled: false, start: "09:00", end: "18:00" },
  });

  // API hooks
  const [getProductById] = useLazyGetProductByIdQuery();
  const [updateProduct] = useUpdateProductMutation();
  const { data: catData } = useGetCatDropDownListQuery({});
  const categoryData = catData?.data?.map(
    (item: { name: string; _id: string }) => ({
      _id: item?._id,
      name: item?.name,
    })
  );
  const [showEditText, setShowEditText] = useState(true);
  // Validation schema
  const validationSchema = Yup.object({
    title: Yup.string()
      .required("Title is required")
      .max(150, "Max 150 characters"),
    price: Yup.number()
      .typeError("Price must be a number")
      .required("Price is required")
      .min(0, "Price cannot be negative"),
    category: Yup.string().required("Category is required"),
    condition: Yup.string()
      .oneOf(["New", "Used"])
      .required("Condition is required"),
    color: Yup.string().nullable(),
    bulkPrice: Yup.number()
      .typeError("Bulk price must be a number")
      .min(0, "Bulk price cannot be negative")
      .nullable(),
    address: Yup.string().required("Address is required"),
    description: Yup.string().required("Description is required"),
    boosterPlan: Yup.string().nullable(),
    image: Yup.string().nullable(),
  });

  const formik = useFormik({
    initialValues: {
      title: "",
      price: "",
      category: "",
      productType: "",
      condition: "New" as "New" | "Used",
      color: "",
      bulkPrice: "",
      boosterPlan: "",
      address: "",
      description: "",
      image: "",
    },
    validationSchema,
    onSubmit: async (values) => {
      try {
        const addrSource = addressObj || {};
        const builtAddress = values.address
          ? { ...addrSource, addressLine1: values.address }
          : addrSource;

        const rawPayload: any = {
          name: values.title,
          price: values.price !== "" ? Number(values.price) : undefined,
          categoryId: values.category || undefined,
          boostPlan: values.boosterPlan || undefined,
          subCategoryId: values.productType || undefined,
          condition: values.condition,
          color: values.color || undefined,
          bulkPrice: Number(values.bulkPrice) || undefined,
          description: values.description,
          address: builtAddress,
          images: values.image ? [values.image] : undefined,
        };

        const payload = prune(rawPayload);

        if (!isEdit || !id) {
          showError("Missing product id to update");
          return;
        }

        const res = await updateProduct({ id, body: payload }).unwrap();
        if (
          res?.code === 200 ||
          (res as any)?.statusCode === 200 ||
          (res as any)?.status === 200
        ) {
          showToast((res as any)?.message || "Product updated successfully");
          navigate("/manage-products");
        } else {
          showError((res as any)?.message || "Failed to update product");
        }
      } catch (e: any) {
        console.error("Error updating product:", e);
        showError(e?.data?.message || "Failed to update product");
      }
    },
  });

  const handleImageUpload = async (event: ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || !files[0]) return;

    const file = files[0];
    if (!file.type.includes("image")) {
      setAlertType(0);
      setShowAlert(true);
      setAlertMessage("This field only accepts images.");
      return;
    }
    try {
      const res = await UploadMedia(file);
      if (res?.code === 200) {
        const imageUrl = res.data;
        setImage(imageUrl);
        setFileName(file.name);
        formik.setFieldValue("image", imageUrl);
        showToast("Image uploaded successfully");
      } else {
        showError(res?.message || "Failed to upload image");
      }
    } catch (err) {
      console.error("Upload error", err);
      showError("Error uploading image. Please try again.");
    }
  };

  useEffect(() => {
    const load = async () => {
      setShowEditText(false);
      if (!isEdit || !id) return;
      try {
        const res = await getProductById(id as any).unwrap();
        const ok =
          res?.code === 200 ||
          (res as any)?.statusCode === 200 ||
          (res as any)?.status === 200;
        if (ok && (res as any)?.data) {
          const data: any = (res as any).data;
          formik.setValues({
            title: data?.title || data?.name || "",
            price: data?.price != null ? String(data.price) : "",
            category:
              data?.categoryId || data?.category?._id || data?.category || "",
            productType: data?.subCategoryId || data?.subcategory?._id || "",
            condition: (data?.condition as any) || "New",
            color: data?.color || "",
            bulkPrice: data?.bulkPrice || "",
            boosterPlan: data?.boostPlan || "",
            address:
              (data?.address &&
                (data.address.addressLine1 || data.address.addressLine2)) ||
              data?.address ||
              "",
            description: data?.description || "",
            image:
              Array.isArray(data?.images) && data.images.length > 0
                ? data.images[0]
                : "",
          });
          setAddressObj(data?.address || null);
          if (Array.isArray(data?.images) && data.images[0])
            setImage(data.images[0]);
        } else {
          showError((res as any)?.message || "Failed to fetch product");
          navigate("/manage-products");
        }
      } catch (e: any) {
        showError(e?.data?.message || "Failed to fetch product");
        navigate("/manage-products");
      } finally {
        setShowEditText(true);
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, id]);

  const toggleAvailability = (day: DayOfWeek) => {
    setAvailability((prev) => ({
      ...prev,
      [day]: { ...prev[day], enabled: !prev[day].enabled },
    }));
  };

  const updateTime = (
    day: DayOfWeek,
    field: "start" | "end",
    value: string
  ) => {
    setAvailability((prev) => ({
      ...prev,
      [day]: { ...prev[day], [field]: value },
    }));
  };

  const { data: subCatResp } = useGetCatDropDownListQuery(
    {
      ...(formik?.values?.category && { parentId: formik?.values?.category }),
    } as any,
    {
      skip: !formik.values.category,
    }
  );
  const subCategoryData = (() => {
    const subRaw: any = subCatResp?.data;
    const list = Array.isArray(subRaw?.docs)
      ? subRaw.docs
      : Array.isArray(subRaw)
        ? subRaw
        : [];
    return list.map((item: { name: string; _id: string }) => ({
      _id: item?._id,
      name: item?.name,
    }));
  })();
  const { data: boostPlans } = useGetBoostPlanDropDownListQuery(
    {
      ...(formik?.values?.category && { categoryId: formik?.values?.category }),
    } as any,
    {
      skip: !formik.values.category,
    }
  );

  const boostPlansData = (() => {
    const subRaw: any = boostPlans?.data?.list;
    console.log(subRaw, "subRaw");
    const list = Array.isArray(subRaw?.docs)
      ? subRaw.docs
      : Array.isArray(subRaw)
        ? subRaw
        : [];
    return list?.map((item: { title: string; _id: string }) => ({
      _id: item?._id,
      name: item?.title,
    }));
  })();

  console.log(formik.values.description, "boostPlans");

  return (
    <>
      <div className="main_title">
        <h1>{isEdit ? "Edit Product" : "Edit Product"}</h1>
        <p>
          <button onClick={() => navigate("/dashboard")}>Dashboard</button> -{" "}
          <button onClick={() => navigate("/manage-products")}>
            Manage Product Listing
          </button>{" "}
          - {isEdit ? "Edit Product" : "Edit Product"}
        </p>
      </div>

      {/* 
      <div className="table_header">
        <div className="left_s">
          <Box className="custom_tabs1" sx={{ width: "100%" }}>
            <Tabs
              value={valueTabs}
              onChange={handleChangeTabs}
              aria-label="basic tabs example"
              className="custom_tabs_links"
            >
              <Tab label="General Store Info" {...a11yProps(0)} />
              <Tab label="Store Availability" {...a11yProps(1)} />
            </Tabs>
          </Box>
        </div>
      </div> 
      */}

      <div className="cards">
        <form className="form" onSubmit={formik.handleSubmit}>
          <div className="gap_p">
            {/* Store Image */}
            <div className="control_group w_100">
              <label htmlFor="store_image">Product Images/Videos</label>
              <div className="gap_p">
                <div className="control_group w_33">
                  {image ? (
                    <div className="upload_image no_radius v2">
                      <div className="upload_image_holder">
                        <figure>
                          <CardMedia
                            component="img"
                            image={image}
                            alt="Uploaded profile photo"
                          />
                        </figure>
                        <IconButton
                          aria-label="Remove uploaded image"
                          onClick={() => {
                            setImage("");
                            setFileName("");
                            formik.setFieldValue("image", "");
                          }}
                          size="small"
                        >
                          <CloseIcon />
                        </IconButton>
                      </div>
                    </div>
                  ) : (
                    <label
                      className="upload_image no_radius v2"
                      htmlFor="store_image"
                    >
                      <Input
                        sx={{ display: "none" }}
                        id="store_image"
                        type="file"
                        inputProps={{ accept: "image/png,image/jpeg" }}
                        onChange={handleImageUpload}
                      />
                      <span className="upload_image_holder">
                        <figure className="add_img">
                          <img
                            src="/static/images/placeholder.png"
                            alt="placeholder"
                          />
                          <span className="upload_img_icon">
                            <img
                              src="/static/images/edit2_icon.svg"
                              alt="Upload"
                            />
                          </span>
                        </figure>
                      </span>
                    </label>
                  )}
                </div>
              </div>
            </div>

            {/* Product Title */}
            <div className="control_group w_33">
              <label htmlFor="store_name">Product Title</label>
              <TextField
                id="store_name"
                name="title"
                placeholder="Enter Product Title"
                fullWidth
                hiddenLabel
                value={formik.values.title}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.title && Boolean(formik.errors.title)}
                helperText={
                  formik.touched.title && (formik.errors.title as any)
                }
              />
            </div>

            <div className="control_group w_33">
              <label htmlFor="price">Price</label>
              <TextField
                id="price"
                name="price"
                type="text"
                placeholder="Enter Price"
                fullWidth
                hiddenLabel
                inputProps={{ inputMode: "decimal" }}
                value={formik.values.price}
                onChange={(e) => {
                  const value = e.target.value;
                  if (/^\d*\.?\d{0,2}$/.test(value)) formik.handleChange(e);
                }}
                onBlur={formik.handleBlur}
                error={formik.touched.price && Boolean(formik.errors.price)}
                helperText={
                  formik.touched.price && (formik.errors.price as any)
                }
              />
            </div>

            <div className="control_group w_33">
              <label htmlFor="region">Category</label>
              <Autocomplete
                disablePortal
                options={categoryData || []}
                getOptionLabel={(o: any) => o.name}
                value={
                  (categoryData || []).find(
                    (c: any) => c._id === formik.values.category
                  ) || null
                }
                onChange={(_, val: any) => {
                  formik.setFieldValue("category", val?._id || "");
                  formik.setFieldValue("productType", "");
                  formik.setFieldValue("boosterPlan", "");
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Select Category"
                    hiddenLabel
                    error={
                      formik.touched.category && Boolean(formik.errors.category)
                    }
                    helperText={
                      formik.touched.category && (formik.errors.category as any)
                    }
                  />
                )}
              />
            </div>
            <div className="control_group w_33">
              <label htmlFor="region">Product Type</label>
              <Autocomplete
                disablePortal
                options={subCategoryData || []}
                getOptionLabel={(o: any) => o.name}
                value={
                  (subCategoryData || []).find(
                    (c: any) => c._id === formik.values.productType
                  ) || null
                }
                onChange={(_, val: any) => {
                  formik.setFieldValue("productType", val?._id || "");
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Select Sub Category"
                    hiddenLabel
                  />
                )}
                disabled={!formik.values.category}
              />
            </div>

            <div className="control_group w_33">
              <label>Condition</label>
              <RadioGroup
                row
                value={formik.values.condition}
                onChange={(e) =>
                  formik.setFieldValue("condition", e.target.value)
                }
              >
                <FormControlLabel value="New" control={<Radio />} label="New" />
                <FormControlLabel
                  value="Used"
                  control={<Radio />}
                  label="Used"
                />
              </RadioGroup>
            </div>

            <div className="control_group w_33">
              <label htmlFor="color">Color</label>
              <Select
                id="color"
                fullWidth
                value={formik.values.color}
                onChange={(e) => formik.setFieldValue("color", e.target.value)}
              >
                <MenuItem disabled value="default">
                  Select Color
                </MenuItem>
                {Object.values(COLORS).map((c) => (
                  <MenuItem key={c} value={c}>
                    {c}
                  </MenuItem>
                ))}
              </Select>
            </div>

            <div className="control_group w_33">
              <label htmlFor="bulk_price">Bulk Price</label>
              <TextField
                id="bulk_price"
                name="bulkPrice"
                type="text"
                placeholder="Enter Bulk Price"
                fullWidth
                hiddenLabel
                inputProps={{ inputMode: "decimal" }}
                value={formik.values.bulkPrice}
                onChange={(e) => {
                  const value = e.target.value;
                  if (/^\d*\.?\d{0,2}$/.test(value)) formik.handleChange(e);
                }}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.bulkPrice && Boolean(formik.errors.bulkPrice)
                }
                helperText={
                  formik.touched.bulkPrice && (formik.errors.bulkPrice as any)
                }
              />
            </div>

            <div className="control_group w_33">
              <label htmlFor="region">Booster Plan</label>
              <Autocomplete
                disablePortal
                options={boostPlansData || []}
                getOptionLabel={(o: any) => o.name}
                value={
                  (boostPlansData || []).find(
                    (c: any) => c._id === formik.values.boosterPlan
                  ) || null
                }
                onChange={(_, val: any) => {
                  formik.setFieldValue("boosterPlan", val?._id || "");
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Select Booster Plan"
                    hiddenLabel
                  />
                )}
                disabled={!formik.values.category}
              />
            </div>

            {/* Address */}
            <div className="control_group w_66">
              <label htmlFor="store_address">Address</label>
              <GoogleAutocomplete
                onLoad={onGoogleLoad}
                onPlaceChanged={onGooglePlaceChanged}
                options={{
                  fields: [
                    "address_components",
                    "geometry",
                    "formatted_address",
                    "name",
                  ],
                }}
              >
                <TextField
                  id="store_address"
                  name="address"
                  placeholder="Enter Address"
                  fullWidth
                  hiddenLabel
                  value={formik.values.address}
                  onChange={(e) => {
                    const v = e.target.value;
                    if (v.trim() === "") {
                      formik.setFieldValue("address", "");
                      setAddressObj((prev: any) =>
                        prev
                          ? { ...prev, addressLine1: "" }
                          : { addressLine1: "" }
                      );
                    } else {
                      formik.handleChange(e);
                      setAddressObj((prev: any) => ({
                        ...(prev || {}),
                        addressLine1: v,
                      }));
                    }
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") e.preventDefault();
                  }}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.address && Boolean(formik.errors.address)
                  }
                  helperText={
                    formik.touched.address && (formik.errors.address as any)
                  }
                />
              </GoogleAutocomplete>
            </div>

            {/* Store Description */}
            <div className="control_group w_100">
              <label htmlFor="store_code">Description</label>
              {showEditText && (
                <EditText
                  content={formik.values.description}
                  setContent={(val: string) =>
                    formik.setFieldValue("description", val)
                  }
                />
              )}
              {formik.touched.description && formik.errors.description ? (
                <div style={{ color: "#d32f2f", fontSize: 12, marginTop: 4 }}>
                  {formik.errors.description as any}
                </div>
              ) : null}
            </div>
          </div>
          <div className="form_btn">
            <Button className="btn btn_primary" type="submit">
              {isEdit ? "Save" : "Submit"}
            </Button>
          </div>
        </form>
      </div>
    </>
  );
};

export default AddProduct;
